import { Label } from "components/label";
import { CollapsibleSection } from "../../../../../components/collapsibleSection";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { HttpError, useOne, useUpdate } from "@refinedev/core";
import { IHomepageDetailData, IOrgIdsItem, IPostData } from "interfaces";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import { useNavigate } from "react-router-dom";
import { get } from "lodash";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import Select from "components/select";
import { useState } from "react";

interface IOrganizerField extends IPostData {
  id: string;
}

const EditOrganizerSchema = z.object({
  id: z.string().min(1, "This field is required"),
});

const EditOrganizersSchema = z.object({
  data: z.array(EditOrganizerSchema).max(9, {
    message: `You can add at most ${9} organizers`,
  }),
});

const MAXIMUM_ORGANIZERS = 4;

export const OrganizersForm = ({
  defaultData,
}: {
  defaultData: IHomepageDetailData["home"]["organizer"];
}) => {
  const [trigger, setTrigger] = useState(false);
  const navigate = useNavigate();

  const { data: orgIds, isLoading: orgIdsLoading } = useOne<
    IOrgIdsItem[],
    HttpError
  >({
    resource: "organization/ids",
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!trigger,
    } as any,
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ data: IPostData[] }, HttpError, { data: IPostData[] }>({
    defaultValues: {
      data: defaultData.data,
    },
    resolver: zodResolver(EditOrganizersSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "data",
  });

  const { mutate, isLoading: isLoading } = useUpdate();

  const onSubmit = (values: { data: IPostData[] }) => {
    const submitValues = values.data.map((value) => `${value.id}`);
    mutate(
      {
        resource: `content/homepage`,
        id: `${defaultData.id}/organizer/home/update`,
        invalidates: [],
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          id: [...submitValues],
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          navigate(`/mkt/homepage/detail?default=organizer`);
        },
      }
    );
  };

  const formIds = watch("data").map((value) => `${value.id}`);

  const organizers = (orgIds?.data ?? [])
    .filter((data) => !formIds.includes(data.id))
    .map((org: IOrgIdsItem) => ({ value: org.id, label: org.name_en }));

  return (
    <CollapsibleSection
      label="Organizers (Max 4 Orgs)"
      defaultOpen={false}
      onClick={() => !trigger && setTrigger(true)}
    >
      <form
        className="divide-y divide-gray-100"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="w-full px-4 py-6 sm:px-6">
          {fields.length < MAXIMUM_ORGANIZERS ? (
            <div className="w-full">
              <Button
                type="button"
                className="w-fit p-2.5 text-sm bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                onClick={() => {
                  append({ position: 0, name: "", id: "" });
                }}
              >
                <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
                <span className="shrink-0 text-sm">Add more</span>
              </Button>
            </div>
          ) : (
            <></>
          )}

          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6 py-4">
            {fields.map((field: IOrganizerField, index: number) => (
              <div className="sm:col-span-2" key={field.id}>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label
                      id={`organizer_${field.id}`}
                      name={`Org Name ${index + 1}`}
                      required
                    />
                    <button onClick={() => remove(index)}>
                      <TrashIcon className="w-5 h-5 text-brand-500" />
                    </button>
                  </div>
                  <Controller
                    control={control}
                    name={`data.${index}.id`}
                    render={({ field: { onChange, value = "", ref } }) => {
                      return (
                        <Select
                          key={`org-id__${field.id}`}
                          inputRef={ref}
                          options={organizers}
                          id="organization_id"
                          placeholder="Select Organization"
                          defaultValue={
                            organizers.find(
                              (selected: { label: string; value: string }) =>
                                selected.value === value
                            ) ??
                            (field.name && field.id)
                              ? {
                                  label: field.name,
                                  value: field.id,
                                }
                              : ""
                          }
                          value={organizers.find(
                            (selected: { label: string; value: string }) =>
                              selected.value === value
                          )}
                          onChange={(selected: {
                            label: string;
                            value: string;
                          }) => {
                            onChange(selected?.value);
                          }}
                          error={get(errors.data, `${index}.id`, "")}
                          isLoading={orgIdsLoading}
                        />
                      );
                    }}
                  />
                  {get(errors.data, `${index}.id`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors.data, `${index}.id.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full px-4 py-4 sm:px-6 bg-white">
          <div className="flex items-center justify-end gap-x-4">
            <Button className="w-20" type="submit">
              SAVE
            </Button>
          </div>
        </div>
      </form>
      {isLoading ? <FullScreenLoading /> : <></>}
    </CollapsibleSection>
  );
};
